import { lazy, Suspense, useMemo, useState } from 'react'
import { BrainCircuit, ChevronRight, FileText, Home, Loader2, RefreshCw, Search, X } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useMemoryTree, useMemoryFile } from '@/hooks/useApi'
import type { MemoryTreeNode } from '@/lib/api'

const KnowledgeExplorer3D = lazy(() => import('./KnowledgeExplorer3D'))

function nodeId(node: MemoryTreeNode) {
  return node.key || node.name
}

function findNode(root: MemoryTreeNode | undefined, id: string): MemoryTreeNode | undefined {
  if (!root) return undefined
  if (nodeId(root) === id) return root
  for (const child of root.children || []) {
    const found = findNode(child, id)
    if (found) return found
  }
}

function collectFiles(node: MemoryTreeNode | undefined, query: string, out: MemoryTreeNode[] = []) {
  if (!node) return out
  const q = query.toLowerCase()
  if (node.type === 'file' && (node.name.toLowerCase().includes(q) || (node.key || '').toLowerCase().includes(q))) out.push(node)
  for (const child of node.children || []) collectFiles(child, query, out)
  return out
}

export default function KnowledgeExplorer() {
  const { data, isLoading, refetch, isFetching } = useMemoryTree()
  const root = data?.tree
  const [currentPath, setCurrentPath] = useState('')
  const [selectedFile, setSelectedFile] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const { data: fileData, isLoading: fileLoading } = useMemoryFile(selectedFile)

  const current = useMemo(() => findNode(root, currentPath) || root, [root, currentPath])
  const visible = useMemo(() => (current?.children || []).slice(0, 14), [current])
  const results = useMemo(() => query.trim().length >= 2 ? collectFiles(root, query.trim()).slice(0, 20) : [], [root, query])
  const crumbs: string[] = (current?.key || current?.name || '').split('/').filter(Boolean)

  const openNode = (node: MemoryTreeNode) => {
    if (node.type === 'file') setSelectedFile(node.key || null)
    else {
      setCurrentPath(nodeId(node))
      setSelectedFile(null)
      setQuery('')
    }
  }

  if (isLoading) return <div className="flex min-h-[420px] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-indigo-500"><BrainCircuit className="h-4 w-4" /> Knowledge Explorer</div>
            <h2 className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">OPS Memory</h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Explore the live knowledge vault spatially, then open the source document without leaving the dashboard.</p>
          </div>
          <div className="flex gap-2">
            <div className="relative min-w-[260px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search memory files..." className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none ring-indigo-500 focus:ring-2 dark:border-slate-700 dark:bg-slate-900" />
            </div>
            <button onClick={() => refetch()} className="rounded-xl border border-slate-200 p-2.5 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-900" title="Refresh"><RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /></button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-1 text-xs text-slate-500">
          <button onClick={() => { setCurrentPath(root ? nodeId(root) : ''); setSelectedFile(null) }} className="rounded-md p-1 hover:bg-slate-100 dark:hover:bg-slate-800"><Home className="h-3.5 w-3.5" /></button>
          {crumbs.map((crumb, i) => <span key={`${crumb}-${i}`} className="flex items-center gap-1"><ChevronRight className="h-3 w-3" /><span>{crumb}</span></span>)}
        </div>
      </div>

      <div className="relative min-h-[570px] bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.10),transparent_42%)] dark:bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.18),transparent_45%)]">
        {query.trim().length >= 2 ? (
          <div className="grid gap-3 p-6 sm:grid-cols-2 xl:grid-cols-3">
            {results.map(node => <button key={nodeId(node)} onClick={() => openNode(node)} className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-300 dark:border-slate-800 dark:bg-slate-900"><FileText className="mb-3 h-5 w-5 text-indigo-500" /><div className="font-semibold text-slate-900 dark:text-white">{node.name}</div><div className="mt-1 truncate text-xs text-slate-500">{node.key || node.name}</div></button>)}
            {!results.length && <div className="col-span-full py-20 text-center text-sm text-slate-500">No matching memory files.</div>}
          </div>
        ) : current ? (
          <Suspense fallback={<div className="flex h-[570px] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>}>
            <div className="hidden h-[620px] md:block"><KnowledgeExplorer3D center={current} nodes={visible} onOpen={openNode} /></div>
            <div className="h-[620px] md:hidden"><KnowledgeExplorer3D center={current} nodes={visible.slice(0, 8)} onOpen={openNode} mobile /></div>
          </Suspense>
        ) : <div className="flex h-[570px] items-center justify-center text-sm text-slate-500">No knowledge nodes available.</div>}
      </div>

      {selectedFile && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm" onClick={() => setSelectedFile(null)}>
        <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-950" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800"><div className="min-w-0"><div className="truncate font-semibold text-slate-950 dark:text-white">{selectedFile.split('/').pop()}</div><div className="truncate text-xs text-slate-500">{selectedFile}</div></div><button onClick={() => setSelectedFile(null)} className="rounded-xl p-2 hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button></div>
          <div className="max-h-[calc(90vh-76px)] overflow-y-auto p-6 sm:p-8">{fileLoading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div> : <article className="prose prose-slate max-w-none dark:prose-invert prose-headings:scroll-mt-20"><ReactMarkdown remarkPlugins={[remarkGfm]}>{fileData?.content || ''}</ReactMarkdown></article>}</div>
        </div>
      </div>}
    </div>
  )
}